/**
 * Notification Center Store (Zustand)
 *
 * Gère l'état du centre de notifications :
 * - Liste des notifications
 * - Compteur non lues
 * - État du drawer
 * - Filtres actifs
 */
import { create } from 'zustand';
import { logger } from '@/utils/logger';
import { notificationsService } from '@/features/notifications/services/notifications.service';
import type {
  Notification,
  NotificationStats,
  NotificationFilters,
  NotificationCategory,
} from '@/features/notifications/types/notification.types';

// ============================================================================
// Types
// ============================================================================

interface NotificationCenterState {
  // Data
  notifications: Notification[];
  unreadCount: number;
  stats: NotificationStats | null;

  // UI State
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  activeCategory: NotificationCategory | null;

  // Pagination
  currentPage: number;
  totalPages: number;
  hasMore: boolean;

  // Computed
  hasUrgent: boolean;

  // Actions - UI
  open: () => void;
  close: () => void;
  toggle: () => void;
  setActiveCategory: (category: NotificationCategory | null) => void;

  // Actions - Data
  fetchNotifications: (filters?: NotificationFilters) => Promise<void>;
  fetchMoreNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchStats: () => Promise<void>;
  refresh: () => Promise<void>;

  // Actions - Mutations
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;

  // Actions - Real-time
  addNotification: (notification: Notification) => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  removeNotification: (id: string) => void;
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;
}

// ============================================================================
// Store
// ============================================================================

export const useNotificationCenterStore = create<NotificationCenterState>((set, get) => ({
  // Initial state
  notifications: [],
  unreadCount: 0,
  stats: null,
  isOpen: false,
  isLoading: false,
  error: null,
  activeCategory: null,
  currentPage: 1,
  totalPages: 1,
  hasMore: false,
  hasUrgent: false,

  // ==================== UI Actions ====================

  open: () => {
    set({ isOpen: true });
    // Fetch fresh data when opening
    get().fetchNotifications();
  },

  close: () => set({ isOpen: false }),

  toggle: () => {
    const { isOpen } = get();
    if (!isOpen) {
      get().open();
    } else {
      get().close();
    }
  },

  setActiveCategory: (category) => {
    set({ activeCategory: category, currentPage: 1 });
    get().fetchNotifications({ category: category || undefined });
  },

  // ==================== Data Actions ====================

  fetchNotifications: async (filters?: NotificationFilters) => {
    set({ isLoading: true, error: null });

    try {
      const { activeCategory } = get();
      const response = await notificationsService.getAll({
        ...filters,
        category: filters?.category || activeCategory || undefined,
        isDismissed: false,
        page: 1,
        limit: 20,
      });

      const hasUrgent = response.data.some(
        (n) => n.priority === 'URGENT' && !n.isRead
      );

      set({
        notifications: response.data,
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.hasMore,
        hasUrgent,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erreur lors du chargement',
        isLoading: false,
      });
    }
  },

  fetchMoreNotifications: async () => {
    const { currentPage, hasMore, isLoading, activeCategory } = get();

    if (!hasMore || isLoading) return;

    set({ isLoading: true });

    try {
      const response = await notificationsService.getAll({
        category: activeCategory || undefined,
        isDismissed: false,
        page: currentPage + 1,
        limit: 20,
      });

      set((state) => ({
        notifications: [...state.notifications, ...response.data],
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.hasMore,
        isLoading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erreur lors du chargement',
        isLoading: false,
      });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      set({ unreadCount: count });
    } catch (error) {
      logger.error('Failed to fetch unread count:', error);
    }
  },

  fetchStats: async () => {
    try {
      const stats = await notificationsService.getStats();
      set({ stats, unreadCount: stats.unread });
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
    }
  },

  refresh: async () => {
    await Promise.all([
      get().fetchNotifications(),
      get().fetchUnreadCount(),
    ]);
  },

  // ==================== Mutation Actions ====================

  markAsRead: async (id: string) => {
    try {
      await notificationsService.markAsRead(id);

      set((state) => {
        const notification = state.notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.isRead;

        return {
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
          ),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        };
      });
    } catch (error) {
      logger.error('Failed to mark as read:', error);
      throw error;
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationsService.markAllAsRead();

      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        })),
        unreadCount: 0,
        hasUrgent: false,
      }));
    } catch (error) {
      logger.error('Failed to mark all as read:', error);
      throw error;
    }
  },

  dismiss: async (id: string) => {
    try {
      await notificationsService.dismiss(id);

      set((state) => {
        const notification = state.notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.isRead;

        return {
          notifications: state.notifications.filter((n) => n.id !== id),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        };
      });
    } catch (error) {
      logger.error('Failed to dismiss:', error);
      throw error;
    }
  },

  // ==================== Real-time Actions ====================

  addNotification: (notification: Notification) => {
    set((state) => {
      // Check if already exists
      if (state.notifications.some((n) => n.id === notification.id)) {
        return state;
      }

      const hasUrgent =
        state.hasUrgent || (notification.priority === 'URGENT' && !notification.isRead);

      // Si un filtre de catégorie est actif, n'ajouter à la liste visible
      // que si la notification correspond à la catégorie filtrée
      const matchesFilter = !state.activeCategory || notification.category === state.activeCategory;

      return {
        notifications: matchesFilter
          ? [notification, ...state.notifications]
          : state.notifications,
        // Le compteur non-lu augmente toujours (indépendant du filtre)
        unreadCount: notification.isRead ? state.unreadCount : state.unreadCount + 1,
        hasUrgent,
      };
    });
  },

  updateNotification: (id: string, updates: Partial<Notification>) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, ...updates } : n
      ),
    }));
  },

  removeNotification: (id: string) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      const wasUnread = notification && !notification.isRead;

      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    });
  },

  setUnreadCount: (count: number) => set({ unreadCount: count }),

  incrementUnreadCount: () =>
    set((state) => ({ unreadCount: state.unreadCount + 1 })),

  decrementUnreadCount: () =>
    set((state) => ({ unreadCount: Math.max(0, state.unreadCount - 1) })),
}));

// ============================================================================
// Selectors (for optimized re-renders)
// ============================================================================

export const selectUnreadCount = (state: NotificationCenterState) => state.unreadCount;
export const selectHasUrgent = (state: NotificationCenterState) => state.hasUrgent;
export const selectIsOpen = (state: NotificationCenterState) => state.isOpen;
export const selectNotifications = (state: NotificationCenterState) => state.notifications;
export const selectIsLoading = (state: NotificationCenterState) => state.isLoading;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for notification bell (minimal re-renders)
 */
export const useNotificationBell = () => {
  const unreadCount = useNotificationCenterStore(selectUnreadCount);
  const hasUrgent = useNotificationCenterStore(selectHasUrgent);
  const toggle = useNotificationCenterStore((state) => state.toggle);
  const fetchUnreadCount = useNotificationCenterStore((state) => state.fetchUnreadCount);

  return { unreadCount, hasUrgent, toggle, fetchUnreadCount };
};

/**
 * Hook for notification center drawer
 */
export const useNotificationCenter = () => {
  return useNotificationCenterStore();
};

export default useNotificationCenterStore;
